import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  IReportsPathParams,
  IReportsQueryParams,
  IReportsCountQueryParams,
  ICreateReport,
  IUpdateReport,
} from '../../shared/models/reports.model';
import { ErrorHandlerService } from './error-handler.service';
import { CompanyService } from './company.service';
import { PermissionService } from './permission.service';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private readonly baseUrl = environment.baseBackendUrl;

  // Variable para almacenar los reportes disponibles de la compañía
  private companyReportsSubject = new BehaviorSubject<any[]>([]);
  public companyReports$ = this.companyReportsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private companyService: CompanyService,
  ) {}

  /**
   * Obtiene todos los Reports con parámetros de consulta opcionales
   * @param params Parámetros de consulta OData
   */
  getReports(params?: IReportsQueryParams): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/odata/Reports`, {
        params: this.buildQueryParams(params),
        headers: this.buildHeaders(),
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Crea un nuevo Report
   * @param reportData Datos del nuevo Report
   */
  createReport(reportData: ICreateReport): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/odata/Reports`, reportData)
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene el conteo de Reports
   * @param params Parámetros de filtro opcionales
   */
  getReportsCount(params?: IReportsCountQueryParams): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/odata/Reports/$count`, {
        params: this.buildQueryParams(params),
        headers: this.buildHeaders(),
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene un Report por key (formato OData)
   * @param key Key del Report
   * @param params Parámetros de consulta opcionales
   */
  getReportByKey(key: number, params?: { $select?: string }): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/odata/Reports(${key})`, {
        params: this.buildQueryParams(params),
        headers: this.buildHeaders(),
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Actualiza un Report por key (formato OData)
   * @param key Key del Report a actualizar
   * @param reportData Datos del Report a actualizar
   */
  updateReportByKey(key: number, reportData: IUpdateReport): Observable<any> {
    return this.http
      .patch<any>(`${this.baseUrl}/odata/Reports(${key})`, reportData)
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Elimina un Report por key (formato OData)
   * @param key Key del Report a eliminar
   */
  deleteReportByKey(key: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/Reports(${key})`).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Obtiene un Report por key (formato REST)
   * @param key Key del Report
   * @param params Parámetros de consulta opcionales
   */
  getReport(key: number, params?: { $select?: string }): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/odata/Reports/${key}`, {
        params: this.buildQueryParams(params),
        headers: this.buildHeaders(),
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Actualiza un Report por key (formato REST)
   * @param key Key del Report a actualizar
   * @param reportData Datos del Report a actualizar
   */
  updateReport(key: number, reportData: IUpdateReport): Observable<any> {
    return this.http
      .patch<any>(`${this.baseUrl}/odata/Reports/${key}`, reportData)
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Elimina un Report por key (formato REST)
   * @param key Key del Report a eliminar
   */
  deleteReport(key: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/Reports/${key}`).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Obtiene las cards de un reporte específico
   * @param key Key del reporte (ej: 'crm')
   * @param params Parámetros de consulta opcionales
   */
  getReportCardsByKey(key: string, params?: any): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .post<any>(`${this.baseUrl}/api/reports/${key}/cards`, params, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene las series de datos para charts de un reporte específico
   * @param key Key del reporte (ej: 'crm')
   * @param params Parámetros de consulta opcionales
   */
  getReportSeriesByKey(key: string, params?: any): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .post<any>(`${this.baseUrl}/api/reports/${key}/series`, params, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene los datos de tabla de un reporte específico
   * @param key Key del reporte (ej: 'crm')
   * @param params Parámetros de consulta opcionales
   */
  getReportTableByKey(key: string, params?: any): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .post<any>(`${this.baseUrl}/api/reports/${key}/table`, params, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene las table keys disponibles para un reporte específico
   */
  getReportTableKeys(key: string): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .get<any>(`${this.baseUrl}/api/reports/${key}/tablekeys`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene las opciones de filtro para un reporte específico
   * @param key Key del reporte (ej: 'crm')
   * @param params Parámetros de filtro incluyendo fechas y otros filtros
   */
  getReportFilters(key: string, params: any): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .post<any>(`${this.baseUrl}/api/reports/${key}/filters`, params, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene los datos generales de detalles de un reporte específico
   * @param key Key del reporte (ej: 'projects')
   * @param id ID del proyecto
   */
  getReportDetails(key: string, id: string): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .get<any>(`${this.baseUrl}/api/reports/${key}/${id}`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene las cards de detalles de un reporte específico
   * @param key Key del reporte (ej: 'projects')
   * @param id ID del proyecto
   */
  getReportDetailsCards(key: string, id: string): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .get<any>(`${this.baseUrl}/api/reports/${key}/${id}/cards`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene las series de datos para charts de los detalles de un reporte específico
   * @param key Key del reporte (ej: 'projects')
   * @param id ID del proyecto
   */
  getReportDetailsSeries(key: string, id: string): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .get<any>(`${this.baseUrl}/api/reports/${key}/${id}/series`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Obtiene los datos de tabla de los detalles de un reporte específico
   * @param key Key del reporte (ej: 'projects')
   * @param id ID del proyecto
   * @param params Parámetros de paginación y ordenamiento
   */
  getReportDetailsTable(
    key: string,
    id: string,
    params?: any,
  ): Observable<any> {
    const headers = this.buildHeaders();
    return this.http
      .post<any>(`${this.baseUrl}/api/reports/${key}/${id}/table`, params, {
        headers,
      })
      .pipe(
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Actualiza el nombre de un reporte
   * @param reportId ID del reporte a actualizar
   * @param newName Nuevo nombre del reporte
   * @returns Observable con la respuesta de la API
   */
  updateReportName(reportId: number, newName: string): Observable<any> {
    const url = `${this.baseUrl}/odata/Reports(${reportId})`;
    const body = { Name: newName };

    return this.http.patch(url, body, {
      headers: this.buildHeaders().set('Content-Type', 'application/json'),
    });
  }

  /**
   * Construye los headers HTTP incluyendo el X-Company-Id
   */
  private buildHeaders(): HttpHeaders {
    const selectedCompany = this.companyService.selectedCompany$.value;
    let headers = new HttpHeaders();

    if (selectedCompany?.id) {
      headers = headers.set('X-Company-Id', selectedCompany.id.toString());
    }

    return headers;
  }

  /**
   * Construye los parámetros de consulta HTTP
   * @param params Parámetros de consulta
   * @returns HttpParams
   */
  private buildQueryParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return httpParams;
  }
}
